/**
 * Data usage for one SIM (task B8).
 *
 * The API caches the upstream answer for `USAGE_CACHE_TTL_MS`, so this offers a
 * manual refresh rather than polling: polling a cached value just burns
 * requests to redisplay the same number, and the operator knows when they want
 * a fresh reading.
 */

import type { UsageSummary } from '@rd/shared';
import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import type { ReactElement } from 'react';

import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorState } from '../../components/ErrorState.js';
import { Skeleton } from '../../components/Skeleton.js';
import { apiRequest } from '../../lib/apiClient.js';

/**
 * How much of the allowance is gone, or `null` when that cannot be known.
 *
 * **Returning `null` is most of the point.** `total` and `used` arrive as free
 * text from the upstream — "500", "1,024 MB", "Unlimited", "On request" — and a
 * meter drawn from a number we guessed at is worse than no meter: it is a
 * confident picture of a fact we do not have. This is the same failure that
 * made an activation quote read "0.00" for a plan priced "on request", where
 * `Number('on request')` silently became `0`.
 *
 * So: strip grouping and units, and if what is left is not a finite number, or
 * the allowance is not a positive quantity, say so by returning `null` and let
 * the caller print the raw strings instead.
 */
export function usageMeter(
  used: string | null,
  total: string | null,
): { fraction: number; over: boolean } | null {
  const parse = (value: string | null): number | null => {
    // `typeof`, not `=== null`. The type says `string | null`, but this value
    // has crossed the wire from an upstream that omits fields it has no answer
    // for — so `undefined` arrives here and the declared type is a promise the
    // runtime does not keep. `value === null` let that through and threw on
    // `.replace`, taking the whole SIM detail page down with it.
    if (typeof value !== 'string') return null;
    const stripped = value.replace(/[^\d.-]/g, '');
    if (stripped === '' || stripped === '-' || stripped === '.') return null;
    const n = Number(stripped);
    return Number.isFinite(n) ? n : null;
  };

  const totalN = parse(total);
  const usedN = parse(used);
  if (totalN === null || usedN === null) return null;
  if (totalN <= 0 || usedN < 0) return null;

  return { fraction: usedN / totalN, over: usedN > totalN };
}

export function UsagePanel({ msisdn }: { msisdn: string | null }): ReactElement {
  const usage = useQuery<UsageSummary>({
    queryKey: ['usage', msisdn],
    queryFn: () => apiRequest<UsageSummary>('/sims/usage', { query: { msisdn: msisdn ?? '' } }),
    enabled: Boolean(msisdn),
    staleTime: 0,
  });

  if (!msisdn) return <EmptyState title="No mobile number" body="Usage needs a mobile number." />;
  if (usage.isPending) return <Skeleton className="h-24 w-full" />;
  if (usage.isError) {
    return <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />;
  }

  const data = usage.data;
  const quotas = Object.entries(data.quotas ?? {});
  const meter = usageMeter(data.used, data.total);

  return (
    <section aria-labelledby="usage-heading" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 id="usage-heading" className="text-sm font-semibold text-body">
          Data usage
        </h2>
        <Button variant="ghost" loading={usage.isFetching} onClick={() => void usage.refetch()}>
          Refresh
        </Button>
      </div>

      {/* A per-SIM problem the upstream reported, already normalised by the API. */}
      {data.problem ? (
        <p role="status" className="text-sm text-warning">
          {data.problem}
        </p>
      ) : null}

      {/*
        The magnitude question the three figures below cannot answer at a
        glance: how much of the allowance is left. Drawn only when both numbers
        are real (see `usageMeter`) — otherwise the figures stand alone.

        The fill is a platform colour, not the tenant's: a meter is a single
        series, so §16.7 ruling C would permit the accent, but a tenant with a
        pale accent would render a bar that cannot be seen against the track.
        Crossing the allowance is a real boundary in the data, not an invented
        threshold, so it — and only it — changes the colour, and it says so in
        words as well.
      */}
      {meter ? (
        <div className="flex flex-col gap-1.5" data-testid="usage-meter">
          <div className="flex items-baseline justify-between text-[length:var(--text-xs)]">
            <span className="text-muted">
              {meter.over ? 'Over allowance' : `${Math.round(meter.fraction * 100)}% of allowance used`}
            </span>
            {meter.over ? (
              <span className="inline-flex items-center gap-1 font-medium text-warning-text">
                <TriangleAlert aria-hidden="true" size={12} strokeWidth={2.25} />
                {data.used} of {data.total} {data.units ?? ''}
              </span>
            ) : null}
          </div>
          <div className="h-2 overflow-hidden rounded-[var(--radius-pill)] bg-surface-alt">
            <div
              className={`h-full rounded-r-[3px] transition-[width] duration-[var(--motion-base)] ${
                meter.over ? 'bg-chart-attention' : 'bg-chart-progress'
              }`}
              style={{ width: `${Math.min(Math.max(meter.fraction * 100, 1.5), 100)}%` }}
            />
          </div>
        </div>
      ) : null}

      <dl className="grid grid-cols-3 gap-3">
        {[
          ['Total', data.total],
          ['Used', data.used],
          ['Remaining', data.remaining],
        ].map(([label, value]) => (
          <div key={label} className="rounded-[var(--brand-radius)] border border-line bg-surface p-3">
            <dt className="text-xs text-muted">{label}</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-body">
              {value ?? '—'}
              {value && data.units ? <span className="ml-1 text-xs text-muted">{data.units}</span> : null}
            </dd>
          </div>
        ))}
      </dl>

      {quotas.length > 0 ? (
        <div className="overflow-x-auto rounded-[var(--brand-radius)] border border-line">
          <table className="w-full border-collapse text-xs">
            <caption className="sr-only">Quota breakdown</caption>
            <thead className="bg-surface-alt">
              <tr>
                {['Quota', 'Allocated', 'Used', 'Available'].map((h) => (
                  <th key={h} scope="col" className="px-2 py-1 text-left font-medium text-muted">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quotas.map(([name, quota]) => (
                <tr key={name} className="border-t border-line bg-surface">
                  <td className="px-2 py-1 text-body">{name}</td>
                  <td className="px-2 py-1 tabular-nums text-body">{quota.allocated ?? '—'}</td>
                  <td className="px-2 py-1 tabular-nums text-body">{quota.used ?? '—'}</td>
                  <td className="px-2 py-1 tabular-nums text-body">{quota.available ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
